/**
 * Mini-terrain tactique « chaîne télé » : les deux équipes positionnées par leur
 * grid API-Football (ligne:colonne), formation + entraîneur. SVG autonome
 * (viewBox 104×68, ratio terrain). Chaque pastille porte data-player (id) +
 * data-side pour la fiche au survol (voir playerStatsTip).
 */
import type { TeamLineup, LineupPlayer } from "../../app/liveSchema.ts";
import { esc, flagCode } from "../html.ts";
import { t } from "../../app/i18n.ts";
import { isAbsent } from "../../app/injuryData.ts";

interface Node {
  readonly p: LineupPlayer;
  readonly x: number;
  readonly y: number;
}

/** Ligne (1 = gardien … N = attaque) + colonne ; repli sur le poste si pas de grid. */
function rowCol(p: LineupPlayer): { row: number; col: number | null } {
  if (p.grid && /^\d+:\d+$/.test(p.grid)) {
    const [r, c] = p.grid.split(":");
    return { row: Number(r), col: Number(c) };
  }
  const byPos: Record<string, number> = { G: 1, D: 2, M: 3, F: 4 };
  return { row: byPos[p.pos ?? ""] ?? 3, col: null };
}

/** Place les 11 sur leur moitié : home = gauche (attaque à droite), away = miroir. */
function layout(players: readonly LineupPlayer[], side: "home" | "away"): Node[] {
  const rc = players.map((p) => ({ p, ...rowCol(p) }));
  const maxRow = Math.max(2, ...rc.map((x) => x.row));
  const byRow = new Map<number, Array<{ p: LineupPlayer; row: number; col: number | null }>>();
  for (const x of rc) {
    const g = byRow.get(x.row);
    if (g) g.push(x);
    else byRow.set(x.row, [x]);
  }
  const out: Node[] = [];
  for (const [row, group] of byRow) {
    group.sort((a, b) => (a.col ?? 99) - (b.col ?? 99));
    const n = group.length;
    const t = maxRow > 1 ? (row - 1) / (maxRow - 1) : 0; // 0 = ligne de but, 1 = attaque
    group.forEach((x, i) => {
      const px = side === "home" ? 6 + t * 38 : 98 - t * 38;
      const py = (68 * (i + 1)) / (n + 1);
      out.push({ p: x.p, x: px, y: py });
    });
  }
  return out;
}

function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? (parts[parts.length - 1] ?? name) : name;
}

function playerNode(node: Node, side: "home" | "away", fill: string, ink: string): string {
  const { p, x, y } = node;
  const id = p.id ? ` data-player="${p.id}"` : "";
  const name = p.name ? ` data-name="${esc(p.name)}"` : "";
  // <g> SVG focusable : sans rôle/nom, un lecteur d'écran n'annonce rien (WCAG 4.1.2).
  const a11y = p.name ? ` role="button" aria-label="${esc(p.name)}${p.captain ? ` — ${t("lineup.captain")}` : ""}"` : "";
  const cap = p.captain
    ? `<circle class="lp-cap" cx="2.7" cy="-2.7" r="1.5"></circle><text class="lp-cap-t" x="2.7" y="-2.15">C</text>`
    : "";
  // Absent/incertain (blessé/suspendu) — rare dans le XI (surtout « Questionable »).
  const abs = typeof p.id === "number" && isAbsent(p.id)
    ? `<circle class="lp-absent" cx="-2.7" cy="-2.7" r="1.5"></circle><text class="lp-absent-t" x="-2.7" y="-2.1">+</text>`
    : "";
  return `<g class="lp-pl lp-${side}"${id}${name}${a11y} data-side="${side}" tabindex="0" transform="translate(${x.toFixed(1)} ${y.toFixed(1)})">
    <circle r="3.2" style="fill:${fill}"></circle>
    <text class="lp-num" y="1.15" style="fill:${ink}">${p.num ?? ""}</text>
    <text class="lp-name" y="6.5">${esc(surname(p.name)).slice(0, 11)}</text>
    ${cap}
    ${abs}
  </g>`;
}

// Marquages blancs du terrain (viewBox 104×68, marge 2).
const PITCH =
  `<rect class="lp-line" x="2" y="2" width="100" height="64" rx="1"></rect>` +
  `<line class="lp-line" x1="52" y1="2" x2="52" y2="66"></line>` +
  `<circle class="lp-line" cx="52" cy="34" r="8"></circle>` +
  `<circle class="lp-spot" cx="52" cy="34" r="0.7"></circle>` +
  // surface gauche
  `<rect class="lp-line" x="2" y="18" width="14" height="32"></rect>` +
  `<rect class="lp-line" x="2" y="27" width="5" height="14"></rect>` +
  `<circle class="lp-spot" cx="12" cy="34" r="0.6"></circle>` +
  `<rect class="lp-goal" x="0.4" y="30" width="1.6" height="8"></rect>` +
  // surface droite (miroir)
  `<rect class="lp-line" x="88" y="18" width="14" height="32"></rect>` +
  `<rect class="lp-line" x="97" y="27" width="5" height="14"></rect>` +
  `<circle class="lp-spot" cx="92" cy="34" r="0.6"></circle>` +
  `<rect class="lp-goal" x="102" y="30" width="1.6" height="8"></rect>`;

/** En-tête : drapeau + formation + entraîneur de chaque équipe. */
function head(homeCode: string, awayCode: string, home: TeamLineup | null, away: TeamLineup | null): string {
  const cell = (code: string, lu: TeamLineup | null, away2: boolean): string =>
    `<div class="lp-team${away2 ? " a" : ""}">
      <span class="f fi fi-${esc(flagCode(code))}"></span>
      <span class="lp-form">${esc(lu?.formation ?? "—")}</span>
      ${lu?.coach ? `<span class="lp-coach">${esc(lu.coach)}</span>` : ""}
    </div>`;
  return `<div class="lp-head">${cell(homeCode, home, false)}<span class="lp-vs">${t("common.vs")}</span>${cell(awayCode, away, true)}</div>`;
}

/** Une puce de remplaçant (numéro + nom), cliquable → fiche joueur si on a son id. */
function benchChip(p: LineupPlayer, side: "home" | "away"): string {
  const id = p.id ? ` data-player="${p.id}"` : "";
  const name = p.name ? ` data-name="${esc(p.name)}"` : "";
  const a11y = p.name ? ` role="button" aria-label="${esc(p.name)}" tabindex="0"` : "";
  const abs = typeof p.id === "number" && isAbsent(p.id) ? ` <span class="lp-sub-abs" title="${t("lineup.absentUncertain")}">+</span>` : "";
  return `<span class="lp-sub lp-${side}"${id}${name}${a11y}><span class="lp-sub-n">${p.num ?? ""}</span><span class="lp-sub-name">${esc(surname(p.name))}${p.captain ? ` <span class="lp-sub-c">C</span>` : ""}${abs}</span></span>`;
}

/** Banc des deux équipes (remplaçants) sous le terrain — vide si aucun sub. */
function benchHtml(home: TeamLineup | null, away: TeamLineup | null): string {
  const h = home?.subs ?? [];
  const a = away?.subs ?? [];
  if (!h.length && !a.length) return "";
  const col = (subs: readonly LineupPlayer[], side: "home" | "away"): string =>
    `<div class="lp-bench-col">${subs.map((p) => benchChip(p, side)).join("")}</div>`;
  return `<div class="lp-bench">
    <div class="lp-bench-h"><i class="ti ti-users" aria-hidden="true"></i> ${t("lineup.substitutes")}</div>
    <div class="lp-bench-cols">${col(h, "home")}${col(a, "away")}</div>
  </div>`;
}

/**
 * Terrain tactique des deux équipes. `homeCode`/`awayCode` servent aux drapeaux.
 * Vide si aucune compo. Les pastilles `[data-player]` alimentent la fiche au survol.
 */
export function lineupPitch(
  homeCode: string,
  awayCode: string,
  home: TeamLineup | null,
  away: TeamLineup | null,
): string {
  if (!home && !away) return "";
  // Couleurs de maillot authentiques (API) avec repli sur les défauts du design.
  const homeFill = home?.colors?.primary ?? "var(--lp-home)";
  const homeInk = home?.colors?.text ?? "#ffffff";
  const awayFill = away?.colors?.primary ?? "var(--lp-away)";
  const awayInk = away?.colors?.text ?? "#15251b";
  const nodes =
    (home ? layout(home.startXI, "home").map((n) => playerNode(n, "home", homeFill, homeInk)).join("") : "") +
    (away ? layout(away.startXI, "away").map((n) => playerNode(n, "away", awayFill, awayInk)).join("") : "");
  return `<div class="lp-wrap">
    ${head(homeCode, awayCode, home, away)}
    <svg class="lp-pitch" viewBox="0 0 104 68" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${t("lineup.startingLineupsAria")}">
      ${PITCH}
      ${nodes}
    </svg>
    ${benchHtml(home, away)}
  </div>`;
}
